import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import MainCard from 'components/MainCard';
import { useEffect, useRef, useState } from 'react';
import { Grid } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import comptroller from 'assets/images/comptroller_white.svg';
import { Col, Form, Input, Row, Spin } from 'antd';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import DetailPageHeader from 'components/header/DetailPageHeader';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import { AssignmentTypeListSelect, AssignmentTypeSelect } from './Create';
import { isEmpty } from 'lodash';
import IdentityActionButton from 'static-solutions/IdentityActionButton';

const Details = () => {
	const { state } = useLocation();
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const activatedOperation = useRef();

	const [modalInfo, setModalInfo] = useState({
		head: 'Process Role',
		action: '',
	});
	const [showModal, setShowModal] = useState(false);
	const [initialValues, setInitialValues] = useState(null);
	const [submitData, setSubmitData] = useState(null);

	const [
		GetSingleIdentity,
		{ data: successData, error: errorData, isSuccess: isSingleCaseSuccess, isError: isSingleCaseError, isLoading: isSingleCaseLoading },
	] = useGetSingleIdentityMutation();

	useEffect(() => {
		if (state?.processCode && state?._id) {
			activatedOperation.current = 'get-single';
			GetSingleIdentity({
				_id: state._id,
				operation: 'get-single',
				processCode: state?.processCode,
				suffixCode: '',
			});
		} else {
			navigate(-1);
		}
	}, [state]);

	useEffect(() => {
		if (isSingleCaseSuccess) {
			if (activatedOperation.current === 'get-single' && isEmpty(initialValues)) {
				setInitialValues({
					...successData,
					positionCode: successData?.data?.positionCode,
					PROPERTY_assignType: successData?.data?.PROPERTY_assignType,
					DATA_processRoleUsers: successData?.data?.DATA_processRoleUsers,
					DATA_processRolePositions: successData?.data?.DATA_processRolePositions,
					assignTypeHidden: successData?.data?.assignTypeHidden,
					assignTypeLabel: successData?.data?.assignTypeLabel,
					assignValue: successData?.data?.assignValue,
					version: successData?.version,
					activationStatus: successData?.activationStatus,
					processVersion: successData?.parkProcessVersion,
					recordStatus: successData?.recordStatus,
					approverUserId: successData?.approverUserId,
					history: successData?.history?.length > 0 ? false : true,
				});
			} else if (activatedOperation.current === 'activate-single') {
				setSubmitData(null);
				toast.success('Success! Action completed ', toastConfig);
				navigate(-1);
			} else if (activatedOperation.current === 'approve-single' || activatedOperation.current === 'reject-single') {
				setSubmitData(null);
				toast.success('Success! Action completed ', toastConfig);
				navigate(-1);
			}
		}
	}, [isSingleCaseSuccess, successData]);

	// console.log(initialValues);

	useEffect(() => {
		if (isSingleCaseError) {
			if (errorData?.data?.code === '501') {
				toast.error(errorData?.data?.message, toastConfig);
			} else {
				toast.error('Failed! Action not completed', toastConfig);
			}
		}
	}, [isSingleCaseError, errorData]);

	const deactivateHandler = (status) => {
		let id = state?._id;
		if (status === null) {
			status = 'Inactive';
		}

		activatedOperation.current = 'activate-single';
		setSubmitData({
			_id: id,
			operation: 'activate-single',
			processCode: state?.processCode,
			suffixCode: '',
			version: initialValues?.version,
			activationStatus: status === 'Active' ? 'Inactive' : 'Active',
		});
		setShowModal(true);
		setModalInfo((prev) => ({
			head: prev.head,
			action: status === 'Active' ? 'deactivate this process role' : 'activate this process role',
		}));
	};

	const approveRejectHandler = (id, operation) => {
		activatedOperation.current = operation;
		setSubmitData({
			_id: id,
			operation,
			processCode: state?.processCode,
			suffixCode: '',
		});
		setShowModal(true);
		setModalInfo((prev) => ({
			head: prev.head,
			action: operation === 'approve-single' ? 'Approve' : 'Reject',
		}));
	};

	const onModalOkHandler = () => {
		if (submitData) {
			GetSingleIdentity(submitData);
		}
	};

	const navigateToEdit = () => {
		navigate('/static/process-role/edit', { state: { processCode: state?.processCode, _id: state?._id, tab: state?.tab } });
	};

	const handleModalSubmit = (data) => {
		setSubmitData(data);
		setShowModal(true);
		setModalInfo({
			head: 'Process Role Master',
			action: 'submit',
		});
	};

	return (
		<Grid container rowSpacing={4.5} columnSpacing={2.75}>
			<Grid item xs={12}>
				<DetailPageHeader title={`View ${state?.processName}`} />

				<Spin spinning={isSingleCaseLoading} style={{ maxHeight: '100%' }}>
					<MainCard>
						{initialValues && (
							<Form
								form={form}
								id="details-process-roles"
								initialValues={initialValues}
								layout="vertical"
								onFinish={handleModalSubmit}
								requiredMark={false}
								disabled
							>
								<AntdValuePreview form={form} show={true} />
								<Row gutter={[16, 0]}>
									<Col xs={24} sm={24} md={24} xl={24}>
										<Form.Item
											label="Process Role Name"
											name="positionCode"
											rules={[
												{
													required: true,
													type: String,
												},
											]}
										>
											<Input placeholder="Enter process role name" />
										</Form.Item>
									</Col>

									<Col xs={24} sm={24} md={24} xl={24}>
										<AssignmentTypeSelect label="Assignment Type" code="PROPERTY_assignType" />
										<Form.Item name="assignTypeHidden" noStyle />
										<Form.Item name="assignTypeLabel" noStyle />
									</Col>

									<Col xs={24} sm={24} md={24} xl={24}>
										<Form.Item
											shouldUpdate={(prevValues, currentValues) =>
												prevValues.PROPERTY_assignType !== currentValues.PROPERTY_assignType
											}
											noStyle
										>
											{({ getFieldValue }) => (
												<>
													{getFieldValue('PROPERTY_assignType') === 'PROPERTY_assignType_fixedAssignmentByProcess' && (
														<AssignmentTypeListSelect
															name="DATA_processRolePositions"
															label="Position ID"
															code="processRolePositions"
														/>
													)}
													{getFieldValue('PROPERTY_assignType') === 'PROPERTY_assignType_tempAssignmentByPractice' && (
														<AssignmentTypeListSelect
															name="DATA_processRoleUsers"
															label="Employee ID"
															code="processRoleUsers"
														/>
													)}
												</>
											)}
										</Form.Item>
									</Col>

									<Col xs={24} sm={24} md={24} xl={24}>
										<Form.Item label="Activation Status" name="activationStatus">
											<Input disabled />
										</Form.Item>
									</Col>
								</Row>

								<Col xs={24}>
									<IdentityActionButton
										{...{
											detail: initialValues,
											history: initialValues?.history,
											id: state?._id,
											processCode: state?.processCode,
											processVersion: initialValues?.processVersion,
											processName: state?.processName,
											activationStatus: initialValues?.activationStatus,
											recordStatus: initialValues?.recordStatus,
											approverUserId: initialValues?.approverUserId,
											version: initialValues?.version,
											deactivateHandlerFunc: deactivateHandler,
											approveRejectHandlerFunc: approveRejectHandler,
											navigateToEditFunc: navigateToEdit,
										}}
									/>
								</Col>
							</Form>
						)}
					</MainCard>
				</Spin>

				{showModal && (
					<ComptrollerConfirmModal
						showModal={showModal}
						setShowModal={setShowModal}
						modalData={modalInfo}
						handleSubmit={onModalOkHandler}
					/>
				)}
			</Grid>
		</Grid>
	);
};

export default Details;
